/**
 * Floating "Enviar comentario" button + modal — private beta feedback.
 * Discreet, reachable from every screen, mobile-first. Auto-collects
 * user/screen/device/browser/version so barbers never type tech data.
 */
import { useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { MessageSquarePlus, ImagePlus, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import { usePwaInstall } from '@/pwa/PwaInstallContext';
import { PWA_BANNER_LIFT_PX } from '@/components/Agenda/AgendaFab';
import { tr } from '@/contexts/LanguageContext';

const APP_VERSION = process.env.REACT_APP_VERSION || '0.1.0-beta';

const PRIORITIES = [
  ['low', 'Baja'],
  ['medium', 'Media'],
  ['high', 'Alta'],
  ['critical', tr("Crítica")],
];

const detectDevice = () => {
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod/i.test(ua)) return 'iOS';
  if (/Android/i.test(ua)) return 'Android';
  if (/Mac/i.test(ua)) return 'Mac';
  if (/Windows/i.test(ua)) return 'Windows';
  if (/Linux/i.test(ua)) return 'Linux';
  return 'Desconocido';
};

const EMPTY = { trying_to_do: '', what_happened: '', suggestion: '', priority: 'medium' };

// M3 (jun-2026) — rutas con botones de acción al fondo donde el widget
// flotante se solapaba (mismo criterio que el FAB global).
const HIDDEN_PREFIXES = ['/seller/', '/admin', '/live/', '/judge/onboarding'];

export const FeedbackButton = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [shot, setShot] = useState(null); // { url, name }
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const inFlight = useRef(false);
  const { bannerVisible } = usePwaInstall();

  if (!user) return null;
  if (HIDDEN_PREFIXES.some((p) => location.pathname.startsWith(p))) return null;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const pickShot = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.postForm('/uploads/media', fd);
      setShot({ url: res.url, name: file.name });
    } catch {
      toast.error(tr("No se pudo adjuntar la captura. Intenta de nuevo."));
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    if (inFlight.current) return;
    if (form.trying_to_do.trim().length < 2 || form.what_happened.trim().length < 2) {
      toast.error(tr("Cuéntanos qué intentabas hacer y qué ocurrió."));
      return;
    }
    inFlight.current = true;
    setSending(true);
    try {
      await api.post('/feedback', {
        ...form,
        screenshot_url: shot?.url || null,
        screen: location.pathname,
        device: detectDevice(),
        browser: navigator.userAgent.slice(0, 480),
        app_version: APP_VERSION,
      });
      toast.success(tr("¡Gracias! Tu comentario fue enviado."));
      setForm(EMPTY);
      setShot(null);
      setOpen(false);
    } catch {
      toast.error(tr("No se pudo enviar. Intenta de nuevo."));
    } finally {
      inFlight.current = false;
      setSending(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed right-3 z-40 flex items-center gap-1.5 rounded-full bg-card/90 backdrop-blur border border-border px-3 py-2 text-[10px] font-semibold text-muted-foreground shadow-lg hover:text-primary hover:border-primary/50 transition-colors"
        style={{ bottom: `calc(10rem + ${bannerVisible ? PWA_BANNER_LIFT_PX : 0}px)` }}
        data-testid="feedback-open-btn"
        aria-label={tr("Enviar comentario")}
      >
        <MessageSquarePlus className="w-3.5 h-3.5" />{tr("Enviar comentario")}</button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="feedback-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-lg">{tr("Ayúdanos a mejorar Ziyup")}</DialogTitle>
            <DialogDescription className="text-xs">{tr("Tu comentario llega directo al equipo. Los datos técnicos se adjuntan solos.")}</DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <Field label={tr("¿Qué estabas intentando hacer?")}>
              <Textarea
                rows={2}
                value={form.trying_to_do}
                onChange={set('trying_to_do')}
                placeholder={tr("Ej: enviar un regalo desde el feed…")}
                data-testid="feedback-trying-input"
              />
            </Field>
            <Field label={tr("¿Qué ocurrió?")}>
              <Textarea
                rows={2}
                value={form.what_happened}
                onChange={set('what_happened')}
                placeholder={tr("Ej: la pantalla se quedó cargando…")}
                data-testid="feedback-happened-input"
              />
            </Field>
            <Field label={tr("¿Cómo podríamos mejorar esta experiencia?")}>
              <Textarea
                rows={2}
                value={form.suggestion}
                onChange={set('suggestion')}
                placeholder={tr("Opcional")}
                data-testid="feedback-suggestion-input"
              />
            </Field>

            <div>
              <p className="text-[11px] font-semibold text-foreground mb-1.5">{tr("Prioridad")}</p>
              <div className="flex gap-1.5">
                {PRIORITIES.map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, priority: key }))}
                    className={`flex-1 text-[11px] font-semibold px-2 py-1.5 rounded-full border transition-colors ${
                      form.priority === key
                        ? 'bg-primary/15 text-primary border-primary/40'
                        : 'bg-background text-muted-foreground border-border'
                    }`}
                    data-testid={`feedback-priority-${key}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <p className="text-[11px] font-semibold text-foreground mb-1.5">{tr("Adjuntar captura de pantalla")}{' '}<span className="text-muted-foreground font-normal">{tr("(opcional)")}</span>
              </p>
              {shot ? (
                <div className="flex items-center gap-2 bg-secondary/60 border border-border rounded-xl px-3 py-2" data-testid="feedback-shot-attached">
                  <img src={shot.url} alt="captura" className="w-10 h-10 rounded-lg object-cover" />
                  <span className="flex-1 text-[11px] text-muted-foreground truncate">{shot.name}</span>
                  <button type="button" onClick={() => setShot(null)} data-testid="feedback-shot-remove" aria-label={tr("Quitar captura")}>
                    <X className="w-4 h-4 text-muted-foreground hover:text-destructive" />
                  </button>
                </div>
              ) : (
                <label className="flex items-center justify-center gap-2 border border-dashed border-border rounded-xl px-3 py-2.5 text-[11px] text-muted-foreground cursor-pointer hover:border-primary/40 transition-colors" data-testid="feedback-shot-label">
                  {uploading
                    ? <><Loader2 className="w-3.5 h-3.5 animate-spin" />{' '}{tr("Subiendo…")}</>
                    : <><ImagePlus className="w-3.5 h-3.5" />{' '}{tr("Elegir imagen")}</>}
                  <input type="file" accept="image/*" className="hidden" onChange={pickShot} disabled={uploading} data-testid="feedback-shot-input" />
                </label>
              )}
            </div>

            <Button
              onClick={submit}
              disabled={sending || uploading}
              className="w-full bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl gap-2"
              data-testid="feedback-submit-btn"
            >{tr("{0} Enviar comentario", sending && <Loader2 className="w-4 h-4 animate-spin" />)}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

const Field = ({ label, children }) => (
  <div>
    <p className="text-[11px] font-semibold text-foreground mb-1.5">{label}</p>
    {children}
  </div>
);

export default FeedbackButton;
